import { Routes } from '@angular/router';
import { LayoutComponent } from './core/layout/layout.component';
import { accessProfilesFeatureGuard } from './modules/access-profiles/guards';
import { authGuard } from './core/guards';
import { domainGuard } from './core/guards/domain.guard';

export const routes: Routes = ([
  {
    path: '',
    pathMatch: 'full',
    loadChildren: () => import('./modules/landing/landing.routes').then((m) => m.LANDING_ROUTES)
  },
  {
    path: 'home',
    redirectTo: '',
    pathMatch: 'full'
  },
  {
    path: 'contato',
    loadChildren: () => import('./modules/landing/landing.routes').then((m) => m.CONTACT_ROUTES)
  },
  {
    path: 'demonstracao',
    loadChildren: () => import('./modules/landing/landing.routes').then((m) => m.DEMO_ROUTES)
  },
  {
    path: 'termos-de-uso',
    loadChildren: () => import('./modules/landing/landing.routes').then((m) => m.TERMS_ROUTES)
  },
  {
    path: 'politica-de-privacidade',
    loadChildren: () => import('./modules/landing/landing.routes').then((m) => m.PRIVACY_ROUTES)
  },
  {
    path: 'auth',
    loadChildren: () => import('./modules/auth/auth.routes').then((m) => m.AUTH_ROUTES)
  },
  {
    path: 'convites/fidelidade/:token',
    loadComponent: () =>
      import('./modules/loyalty/features/invitation-landing/invitation-landing.component').then(
        (module) => module.LoyaltyInvitationLandingComponent
      )
  },
  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'assinatura/confirmada',
        loadComponent: () =>
          import('./modules/billing/pages/subscription-confirmation-page.component').then(
            (module) => module.SubscriptionConfirmationPageComponent
          ),
        canActivate: [authGuard],
        data: { module: 'billing' }
      },
      {
        path: 'assinatura',
        loadComponent: () =>
          import('./modules/billing/pages/subscription-page.component').then(
            (module) => module.SubscriptionPageComponent
          ),
        canActivate: [authGuard],
        data: { module: 'billing' }
      },
      {
        path: 'comissoes',
        loadChildren: () =>
          import('./modules/commissions/commissions.routes').then((m) => m.COMMISSIONS_ROUTES),
        canActivate: [authGuard],
        data: { module: 'commissions' }
      },
      {
        path: 'clientes',
        loadChildren: () => import('./modules/customers/routes').then((m) => m.CUSTOMERS_ROUTES),
        canActivate: [authGuard],
        data: { module: 'customers' }
      },
      {
        path: 'fornecedores',
        loadChildren: () => import('./modules/suppliers/routes').then((m) => m.SUPPLIER_ROUTES),
        canActivate: [authGuard],
        data: { module: 'suppliers' }
      },
      {
        path: 'categorias-de-produto',
        loadChildren: () =>
          import('./modules/product-category/routes').then((m) => m.PRODUCT_CATEGORY_ROUTES),
        canActivate: [authGuard],
        data: { module: 'products' }
      },
      {
        path: 'produtos',
        loadChildren: () => import('./modules/products/routes').then((m) => m.PRODUCTS_ROUTES),
        canActivate: [authGuard],
        data: { module: 'products' }
      },
      {
        path: 'marcas',
        loadChildren: () => import('./modules/brands/routes').then((m) => m.BRANDS_ROUTES),
        canActivate: [authGuard],
        data: { module: 'products' }
      },
      {
        path: 'unidades-de-medida',
        loadChildren: () =>
          import('./modules/units-of-measure/routes').then((m) => m.UNITS_OF_MEASURE_ROUTES),
        canActivate: [authGuard],
        data: { module: 'products' }
      },
      {
        path: 'vendas',
        loadChildren: () => import('./modules/sales/routes').then((m) => m.SALES_ROUTES),
        canActivate: [authGuard],
        data: { module: 'sales' }
      },
      {
        path: 'compras',
        loadChildren: () => import('./modules/purchases/routes').then((m) => m.PURCHASE_ROUTES),
        canActivate: [authGuard],
        data: { module: 'purchases' }
      },
      {
        path: 'financeiro',
        loadChildren: () => import('./modules/financial/routes').then((m) => m.FINANCIAL_ROUTES),
        canActivate: [authGuard],
        data: { module: 'financial' }
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./modules/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES),
        canActivate: [authGuard],
        data: { module: 'dashboard' }
      },
      {
        path: 'estoque',
        loadChildren: () =>
          import('./modules/stock-movement/stock-movement.routes').then(
            (m) => m.STOCK_MOVEMENT_ROUTES
          ),
        canActivate: [authGuard],
        data: { module: 'inventory' }
      },
      {
        path: 'movimentacoes-de-estoque',
        loadChildren: () =>
          import('./modules/stock-movement/stock-movement.routes').then(
            (m) => m.STOCK_MOVEMENT_ROUTES
          ),
        canActivate: [authGuard],
        data: { module: 'inventory' }
      },
      {
        path: 'relatorios',
        loadChildren: () =>
          import('./modules/reports/reports.routes').then((m) => m.REPORTS_ROUTES),
        canActivate: [authGuard],
        data: { module: 'reports' }
      },
      {
        path: 'configuracoes',
        canActivate: [authGuard],
        data: { module: 'settings' },
        children: [
          { path: '', redirectTo: 'perfil', pathMatch: 'full' },
          {
            path: ':section',
            loadComponent: () =>
              import('./modules/settings/pages/settings/settings.component').then(
                (c) => c.SettingsComponent
              )
          }
        ]
      },
      {
        path: 'perfil',
        loadComponent: () =>
          import('./modules/profile/pages/profile/profile.component').then(
            (c) => c.ProfileComponent
          ),
        canActivate: [authGuard]
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./modules/notifications/pages/notifications/notifications.component').then(
            (m) => m.NotificationsComponent
          ),
        canActivate: [authGuard]
      },
      {
        path: 'usuarios',
        loadChildren: () => import('./modules/users/routes').then((m) => m.USERS_ROUTES),
        canActivate: [authGuard],
        data: { module: 'users' }
      },
      {
        path: 'perfis-de-acesso',
        loadChildren: () =>
          import('./modules/access-profiles/routes').then((m) => m.ACCESS_PROFILES_ROUTES),
        canActivate: [authGuard, accessProfilesFeatureGuard],
        data: { module: 'users' }
      },
      {
        path: 'permissoes',
        loadChildren: () =>
          import('./modules/permissions/routes').then((m) => m.PERMISSIONS_ROUTES),
        canActivate: [authGuard],
        data: { module: 'users' }
      },
      {
        path: 'acesso-negado',
        loadComponent: () =>
          import('./modules/access-denied/pages/access-denied/access-denied.component').then(
            (m) => m.AccessDeniedComponent
          )
      },
      {
        path: 'fidelidade',
        loadChildren: () =>
          import('./modules/loyalty/loyalty.routes').then((m) => m.LOYALTY_ROUTES),
        canActivate: [authGuard],
        data: { module: 'loyalty' }
      },
      {
        path: '**',
        loadChildren: () =>
          import('./modules/not-found/not-found.routes').then((m) => m.NOT_FOUND_ROUTES)
      }
    ],
    canActivate: [authGuard]
  }
] satisfies Routes).map((route) => ({ ...route, canMatch: [domainGuard] }));
